import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Mic } from 'lucide-react';
import {
  getMyVoiceSetting,
  saveVoiceSetting,
  getRecentVoiceDonations,
  RecentVoiceDonation,
  VoiceSetting,
} from '../../../api/payment/donate-voice.api';
import { formatCa } from '../../../utils/donate-alert';
import { parseServerDate } from '../../../utils/server-date';

const INPUT = 'ui-input h-11 w-full px-3 text-base';

const formatDateTime = (iso?: string | null) => {
  const d = parseServerDate(iso);
  if (!d) return '';
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/** Số ngày còn lại tới khi file ghi âm bị dọn (làm tròn lên, tối thiểu 0). */
const daysLeft = (expiresAt?: string | null): number => {
  const exp = parseServerDate(expiresAt);
  if (!exp) return 0;
  return Math.max(0, Math.ceil((exp.getTime() - Date.now()) / 86400000));
};

/**
 * "Lời nhắn giọng nói": streamer tự bật/tắt nhận kèm ghi âm khi donate, đặt mức Ca
 * tối thiểu, và xem lại những lời nhắn giọng nói gần đây người xem đã gửi.
 */
const VoiceMessageSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [enabled, setEnabled] = useState(false);
  const [minCaInput, setMinCaInput] = useState('20000');
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState<RecentVoiceDonation[]>([]);

  const applySetting = (s: VoiceSetting) => {
    setEnabled(!!s?.enabled);
    setMinCaInput(`${s?.minCa ?? 0}`);
  };

  const load = () => {
    setLoadError(false);
    getMyVoiceSetting()
      .then((s) => {
        applySetting(s);
        setLoaded(true);
      })
      .catch(() => setLoadError(true));
    getRecentVoiceDonations()
      .then((list) => setRecent(list || []))
      .catch(() => setRecent([]));
  };

  useEffect(() => {
    if (!userId) return;
    load();
  }, [userId]);

  const handleSave = () => {
    if (saving) return; // chặn bấm hai lần
    const minCa = Math.max(0, Math.round(Number(minCaInput) || 0));
    setSaving(true);
    saveVoiceSetting({ enabled, minCa })
      .then((s) => {
        applySetting(s);
        toast.success('Đã lưu');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Lưu không thành công'))
      .finally(() => setSaving(false));
  };

  return (
    <div className="ui-card mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full ui-heading font-semibold text-[22px] leading-[27px]">
        <Mic size={20} className="text-blue-600" />
        Lời nhắn giọng nói
      </p>

      <div className="w-full flex flex-col gap-4 ui-surface-2 p-3">
        <p className="text-sm ui-muted">
          Lời nhắn giọng nói tối đa 15 giây, phát ngay sau thông báo donate trên live. Cab giữ lại 30 ngày để
          xử lý khi có báo cáo.
        </p>

        {loadError && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <span>Không tải được cài đặt đã lưu, bạn thử lại nhé.</span>
            <button type="button" className="font-medium underline" onClick={load}>
              Thử lại
            </button>
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          <span className="text-sm ui-text">Cho phép người xem gửi kèm lời nhắn giọng nói</span>
        </label>

        <div className="flex flex-col gap-1 max-w-xs">
          <label className="text-sm font-medium ui-heading" htmlFor="voice-min-ca">
            Mức tối thiểu (Ca)
          </label>
          <input
            id="voice-min-ca"
            type="number"
            min={0}
            step={1}
            value={minCaInput}
            onChange={(e) => setMinCaInput(e.target.value)}
            className={INPUT}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={handleSave}
            className="h-11 rounded-lg bg-[#39539E] px-6 text-white disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu'}
          </button>
        </div>

        <div className="flex flex-col gap-2 border-t ui-divider pt-3">
          <span className="text-sm font-medium ui-heading">Lời nhắn giọng nói gần đây</span>
          {recent.length === 0 ? (
            <p className="text-sm ui-muted">Chưa có lời nhắn giọng nói nào.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {recent.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-1 rounded-lg border ui-divider ui-surface p-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm ui-text">
                      {r.senderName}
                      {r.isAnonymous && (
                        <span className="ml-1 rounded ui-surface-2 px-1 py-0.5 text-[10px] ui-muted align-middle">
                          ẩn danh
                        </span>
                      )}{' '}
                      · {formatCa(r.amount)}
                    </span>
                    <span className="text-xs ui-muted">
                      {formatDateTime(r.createdAt)} · {r.url ? `còn ${daysLeft(r.expiresAt)} ngày` : 'đã xoá'}
                    </span>
                  </div>
                  {r.url && (
                    <audio controls preload="none" src={r.url} className="h-8 sm:w-56">
                      <track kind="captions" />
                    </audio>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceMessageSetting;
